"use client";

import { useEffect } from "react";

interface WovnTranslationProtectorProps {
  enabled?: boolean;
  locale?: string;
}

export default function WovnTranslationProtector({
  enabled = true,
  locale,
}: WovnTranslationProtectorProps) {
  useEffect(() => {
    if (!enabled || process.env.NODE_ENV === "development") {
      return;
    }

    const translatedContentMap = new Map<
      string,
      { content: string; timestamp: number; userModified: boolean }
    >();
    const protectedElements = new Set<Element>();
    const lastScanTime = { value: 0 };
    const lastUserInteraction = { value: 0 };

    // Track user interactions to identify legitimate updates
    const updateUserInteraction = () => {
      lastUserInteraction.value = Date.now();
    };

    // Replace .forEach with for...of for event listeners
    for (const event of ["click", "keydown", "input", "change"]) {
      document.addEventListener(event, updateUserInteraction, {
        passive: true,
      });
    }

    function hasWovnTranslation(element: Element): boolean {
      const innerHTML = (element.innerHTML || "").toString();
      return (
        typeof innerHTML === "string" && innerHTML.includes("<!--wovn-src:")
      );
    }

    function getElementSignature(element: Element): string {
      const tagName = element.tagName;
      const textContent = (element.textContent?.trim() || "").toString();
      const parent = element.parentElement?.tagName || "";
      const className = (element.className || "").toString();
      const index = Array.from(element.parentElement?.children || []).indexOf(
        element,
      );

      const isSelectElement =
        (typeof className === "string" &&
          className.includes("custom-select")) ||
        element?.closest('[class*="custom-select"]');
      const selectContext = isSelectElement ? "select" : "";

      return `${tagName}-${parent}-${selectContext}-${index}-${className.substring(0, 30)}-${textContent.substring(0, 50)}`;
    }

    function getOriginalText(element: Element): string | null {
      const innerHTML = (element.innerHTML || "").toString();
      const regex = /<!--wovn-src:([^-->]+)-->/;
      const match = regex.exec(innerHTML);
      return match ? match[1] : null;
    }

    function isRecentUserInteraction(): boolean {
      return Date.now() - lastUserInteraction.value < 2000; // 2 seconds
    }

    function isLegitimateUpdate(element: Element, newContent: string): boolean {
      const signature = getElementSignature(element);
      const stored = translatedContentMap.get(signature);

      if (!stored) return true;

      if (isRecentUserInteraction()) {
        return true;
      }

      const oldTextContent = (stored.content || "")
        .toString()
        .replaceAll(/<!--wovn-src:[^-->]+-->/g, "");
      const newTextContent = (newContent || "")
        .toString()
        .replaceAll(/<!--wovn-src:[^-->]+-->/g, "");

      const similarity = calculateSimilarity(oldTextContent, newTextContent);
      if (similarity < 0.7) {
        return true;
      }

      if (stored.userModified) {
        return true;
      }

      return false;
    }

    function calculateSimilarity(str1: string, str2: string): number {
      const len1 = str1.length;
      const len2 = str2.length;
      if (len1 === 0 && len2 === 0) return 1;
      if (len1 === 0 || len2 === 0) return 0;

      const longer = len1 > len2 ? str1 : str2;
      const shorter = len1 > len2 ? str2 : str1;

      if (longer.length === 0) return 1;

      const editDistance = levenshteinDistance(longer, shorter);
      return (longer.length - editDistance) / longer.length;
    }

    function levenshteinDistance(str1: string, str2: string): number {
      const matrix = new Array(str2.length + 1)
        .fill(null)
        .map(() => new Array(str1.length + 1).fill(null));

      for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
      for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

      for (let j = 1; j <= str2.length; j++) {
        for (let i = 1; i <= str1.length; i++) {
          const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
          matrix[j][i] = Math.min(
            matrix[j][i - 1] + 1,
            matrix[j - 1][i] + 1,
            matrix[j - 1][i - 1] + indicator,
          );
        }
      }

      return matrix[str2.length][str1.length];
    }

    function checkForRerenderedElement(element: Element): boolean {
      const signature = getElementSignature(element);
      const stored = translatedContentMap.get(signature);

      if (stored && !(element as HTMLElement).dataset.wovnProtected) {
        const originalText = getOriginalText({
          innerHTML: stored.content,
        } as Element);

        const elementInnerHTML = (element.innerHTML || "").toString();
        if (
          originalText &&
          elementInnerHTML.includes(originalText) &&
          !elementInnerHTML.includes("<!--wovn-src:") &&
          elementInnerHTML !== stored.content &&
          !isLegitimateUpdate(element, elementInnerHTML)
        ) {
          try {
            element.innerHTML = stored.content;
            preserveTranslatedContent(element);
            return true;
          } catch (error) {
            console.warn(error);
          }
        } else if (hasWovnTranslation(element)) {
          preserveTranslatedContent(element);
          return true;
        }
      }

      return false;
    }

    function getTextElements(): Element[] {
      const prioritySelectors = [
        ".custom-select__placeholder",
        ".custom-select__single-value",
        "[data-richtext-id]",
        "p",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
      ];

      const standardSelectors = [
        "span",
        "li",
        "td",
        "th",
        "label",
        "button",
        "a",
      ];

      const elements: Element[] = [];

      // Use for...of instead of forEach
      for (const selector of prioritySelectors) {
        try {
          const found = document.querySelectorAll(selector);
          for (const el of found) {
            const textContent = (el.textContent || "").toString();
            if (textContent && textContent.trim().length > 0) {
              elements.push(el);
            }
          }
        } catch (error) {
          console.warn(error);
        }
      }

      // Use for...of instead of forEach
      for (const selector of standardSelectors) {
        const found = document.querySelectorAll(selector);
        for (const el of found) {
          const textContent = (el.textContent || "").toString();
          if (
            textContent &&
            textContent.trim().length > 0 &&
            !elements.includes(el)
          ) {
            elements.push(el);
          }
        }
      }

      return elements;
    }

    function preserveTranslatedContent(element: Element): void {
      const signature = getElementSignature(element);
      const currentHTML = (element.innerHTML || "").toString();

      if (
        protectedElements.has(element) &&
        (element as HTMLElement).dataset.wovnProtected
      ) {
        return;
      }

      translatedContentMap.set(signature, {
        content: currentHTML,
        timestamp: Date.now(),
        userModified: isRecentUserInteraction(),
      });
      (element as HTMLElement).dataset.wovnProtected = "true";
      protectedElements.add(element);

      const preservedContent = currentHTML;

      try {
        Object.defineProperty(element, "innerHTML", {
          get: function () {
            return preservedContent;
          },
          set: function (value: string) {
            const stack = (
              new Error("Wovn innerHTML stack").stack || ""
            ).toString();
            const isWovnCall =
              stack.includes("wovn") ||
              stack.includes("j.wovn.io") ||
              stack.includes("translate");

            if (!isWovnCall && isLegitimateUpdate(element, value)) {
              Object.defineProperty(this, "innerHTML", {
                value: value,
                writable: true,
                configurable: true,
              });

              translatedContentMap.set(signature, {
                content: value,
                timestamp: Date.now(),
                userModified: true,
              });
              return;
            }

            if (isWovnCall && (element as HTMLElement).dataset.wovnProtected) {
              return;
            }

            const originalText = getOriginalText(element);
            const valueStr = (value || "").toString();
            if (
              originalText &&
              valueStr.includes(originalText) &&
              !valueStr.includes("<!--wovn-src:") &&
              !isLegitimateUpdate(element, valueStr)
            ) {
              return;
            }

            Object.defineProperty(this, "innerHTML", {
              value: value,
              writable: true,
              configurable: true,
            });

            if (!isWovnCall) {
              translatedContentMap.set(signature, {
                content: value,
                timestamp: Date.now(),
                userModified: isRecentUserInteraction(),
              });
            }
          },
          configurable: true,
        });
      } catch (error) {
        console.warn(error);
      }
    }

    function restoreTranslatedContent(element: Element): boolean {
      const signature = getElementSignature(element);
      const stored = translatedContentMap.get(signature);

      const elementInnerHTML = (element.innerHTML || "").toString();
      if (stored && stored.content !== elementInnerHTML) {
        if (!isLegitimateUpdate(element, elementInnerHTML)) {
          try {
            Object.defineProperty(element, "innerHTML", {
              value: stored.content,
              writable: true,
              configurable: true,
            });
            preserveTranslatedContent(element);
            return true;
          } catch (error) {
            console.warn(error);
          }
        }
      }
      return false;
    }

    function scanAndProtectElements(isHighPriority = false): number {
      const now = Date.now();

      if (!isHighPriority && now - lastScanTime.value < 300) {
        return 0;
      }

      lastScanTime.value = now;

      const elements = getTextElements();
      let protectedCount = 0;
      let restoredCount = 0;

      for (const element of elements) {
        if (checkForRerenderedElement(element)) {
          restoredCount++;
          continue;
        }

        const signature = getElementSignature(element);

        if (
          hasWovnTranslation(element) &&
          !(element as HTMLElement).dataset.wovnProtected
        ) {
          preserveTranslatedContent(element);
          protectedCount++;
        } else if (
          translatedContentMap.has(signature) &&
          !(element as HTMLElement).dataset.wovnProtected
        ) {
          const originalText = getOriginalText(element);

          const elementInnerHTML = (element.innerHTML || "").toString();
          if (
            originalText &&
            elementInnerHTML.includes(originalText) &&
            !elementInnerHTML.includes("<!--wovn-src:") &&
            !isLegitimateUpdate(element, elementInnerHTML)
          ) {
            if (restoreTranslatedContent(element)) {
              restoredCount++;
            }
          } else if (hasWovnTranslation(element)) {
            preserveTranslatedContent(element);
            protectedCount++;
          }
        }
      }

      return protectedCount + restoredCount;
    }

    const originalMutationObserver = globalThis.MutationObserver;
    let isMonitoringActive = true;

    globalThis.MutationObserver = class extends originalMutationObserver {
      public constructor(callback: MutationCallback) {
        const enhancedCallback: MutationCallback = (mutations, observer) => {
          if (isMonitoringActive) {
            for (const mutation of mutations) {
              if (
                mutation.type === "characterData" ||
                (mutation.type === "childList" &&
                  mutation.addedNodes.length > 0)
              ) {
                const target =
                  mutation.target.nodeType === Node.ELEMENT_NODE
                    ? (mutation.target as Element)
                    : mutation.target.parentElement;

                if (target) {
                  checkForRerenderedElement(target);

                  if (mutation.type === "childList") {
                    for (const node of mutation.addedNodes) {
                      if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as Element;
                        checkForRerenderedElement(element);

                        const children = element.querySelectorAll("*");
                        for (const child of children) {
                          checkForRerenderedElement(child);
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          return callback(mutations, observer);
        };

        super(enhancedCallback);
      }
    };

    scanAndProtectElements(true);

    const monitor = setInterval(() => {
      scanAndProtectElements(false);
    }, 1000);

    const observer = new MutationObserver((mutations) => {
      let hasHighPriorityChanges = false;
      let hasTextChanges = false;

      for (const mutation of mutations) {
        if (mutation.type === "characterData") {
          hasTextChanges = true;
        } else if (
          mutation.type === "childList" &&
          mutation.addedNodes.length > 0
        ) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              const className = (element.className || "").toString();
              if (
                className.includes("custom-select") ||
                hasWovnTranslation(element) ||
                translatedContentMap.has(getElementSignature(element))
              ) {
                hasHighPriorityChanges = true;
              }
            }
          }
        }
      }

      if (hasTextChanges) {
        scanAndProtectElements(true);
      } else if (hasHighPriorityChanges) {
        setTimeout(() => scanAndProtectElements(true), 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const selectObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const targetElement = mutation.target as Element;
        const className = (targetElement.className || "").toString();

        if (
          mutation.type === "attributes" &&
          className.includes("custom-select")
        ) {
          setTimeout(() => scanAndProtectElements(true), 50);
        }
      }
    });

    const selectContainers = document.querySelectorAll(
      '[class*="custom-select"]',
    );
    const selectContainersArray = Array.from(selectContainers);

    for (const container of selectContainersArray) {
      selectObserver.observe(container, {
        attributes: true,
        subtree: true,
        attributeFilter: ["class"],
      });
    }

    return () => {
      clearInterval(monitor);
      observer.disconnect();
      selectObserver.disconnect();

      // Cleanup event listeners
      for (const event of ["click", "keydown", "input", "change"]) {
        document.removeEventListener(event, updateUserInteraction);
      }

      isMonitoringActive = false;
      globalThis.MutationObserver = originalMutationObserver;
    };
  }, [enabled, locale]);

  useEffect(() => {
    if (!enabled || process.env.NODE_ENV === "development") {
      return;
    }
  }, [locale, enabled]);

  return null;
}
